import { useMemo, useState } from "react";
import { Bell, Mail } from "lucide-react";

import { Badge, Card, CardContent } from "@exhousing/design-system";

import {
  useNotificationPreview,
  useNotifications,
} from "@/hooks/api/useNotifications";

type Item = {
  id: string;
  name: string;
  when: string;
  audience: string;
  channel: string;
  languages: string[];
};

const AUDIENCE_ORDER = [
  "candidate",
  "tenant",
  "landlord",
  "staff",
  "subscriber",
] as const;

const AUDIENCE_LABEL: Record<string, string> = {
  candidate: "Candidates",
  tenant: "Tenants",
  landlord: "Landlords",
  staff: "Your team",
  subscriber: "Subscribers",
};

const LANGUAGE_LABEL: Record<string, string> = { nl: "Dutch", en: "English" };

export function NotificationsPage() {
  const { data: items, isLoading } = useNotifications();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [language, setLanguage] = useState<string>("nl");

  const grouped = useMemo(() => {
    const list = (items ?? []) as Item[];
    return AUDIENCE_ORDER.map((audience) => ({
      audience,
      items: list.filter((i) => i.audience === audience),
    })).filter((g) => g.items.length > 0);
  }, [items]);

  const selected = useMemo(
    () => ((items ?? []) as Item[]).find((i) => i.id === selectedId) ?? null,
    [items, selectedId],
  );

  const open = (item: Item) => {
    setSelectedId(item.id);
    setLanguage(item.languages.includes(language) ? language : item.languages[0]);
  };

  const { data: preview, isLoading: previewLoading } = useNotificationPreview(
    selected?.id,
    language,
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">
          Notifications
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Every automatic e-mail Balatin sends on your behalf, and exactly how it
          reads. These messages are managed for you — sent at the right moment, in
          the recipient&rsquo;s language, with consistent branding.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Left: the catalog, grouped by who receives it. */}
        <Card>
          <CardContent className="space-y-5 pt-6">
            {isLoading && (
              <p className="text-sm text-muted-foreground">Loading…</p>
            )}
            {grouped.map((group) => (
              <div key={group.audience} className="space-y-1">
                <p className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {AUDIENCE_LABEL[group.audience] ?? group.audience}
                </p>
                {group.items.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => open(item)}
                    className={`flex w-full flex-col gap-0.5 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                      selected?.id === item.id
                        ? "bg-secondary font-medium"
                        : "hover:bg-muted"
                    }`}
                  >
                    <span>{item.name}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {item.when}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Right: the email itself. */}
        <Card>
          <CardContent className="pt-6">
            {!selected ? (
              <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
                <Bell className="size-8 text-muted-foreground/50" />
                <p className="text-sm text-muted-foreground">
                  Select a notification to see exactly what is sent.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-1">
                    <h2 className="font-display text-lg font-medium">
                      {selected.name}
                    </h2>
                    <p className="text-sm text-muted-foreground">{selected.when}</p>
                  </div>
                  <Badge tone="neutral">Managed by Balatin</Badge>
                </div>

                {/* Language toggle, only when the mail is bilingual. */}
                {selected.languages.length > 1 && (
                  <div className="inline-flex rounded-md border border-input p-0.5">
                    {selected.languages.map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setLanguage(lang)}
                        className={`rounded px-3 py-1 text-xs font-medium transition-colors ${
                          language === lang
                            ? "bg-secondary text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {LANGUAGE_LABEL[lang] ?? lang}
                      </button>
                    ))}
                  </div>
                )}

                {/* Subject line — what lands in the inbox. */}
                <div className="flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm">
                  <Mail className="size-4 shrink-0 text-muted-foreground" />
                  <span className="text-muted-foreground">Subject:</span>
                  <span className="font-medium">
                    {previewLoading ? "…" : (preview?.subject ?? "")}
                  </span>
                </div>

                {/* The rendered e-mail, sandboxed and read-only. */}
                <div className="overflow-hidden rounded-md border border-input bg-white">
                  <iframe
                    title={`${selected.name} preview`}
                    srcDoc={preview?.html ?? ""}
                    sandbox=""
                    className="block w-full"
                    style={{ height: 620 }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
